import { generateUUID } from '@/domain/shared/generateUUID';
import type { TFieldJSONData } from '@/domain/field/TFieldJSONData';
import type { TFieldKind } from '@/domain/field/TFieldKind';
import type { TPosition2D } from '@/domain/shared/TPosition2D';
import type { TScale2D } from '@/domain/shared/TScale2D';
import type { TSide } from '@/domain/shared/TSide';
import type { TSize2D } from '@/domain/shared/TSize2D';

type TAFieldParameters = {
    frameSize: TSize2D | undefined;
    // Accepted so a field rebuilt from JSON keeps its identity without a post-construction
    // write, which is what lets `id` be readonly. Absent means "a new field": a fresh uuid.
    id?: string;
    isLocked: boolean;
    position: TPosition2D;
    rotationDeg: number;
    scale: TScale2D;
    side: TSide;
};

abstract class AField {
    abstract readonly kind: TFieldKind;
    // Dimensions of the canvas frame this field was originally
    // positioned in — snapshotted at add time. Consumed by BAT
    // preview / material updater when the canvas is rendered at a
    // different scale (undefined for fields created without a
    // reference frame).
    frameSize: TSize2D | undefined;
    readonly id: string;
    isLocked: boolean;
    position: TPosition2D;
    rotationDeg: number;
    scale: TScale2D;
    side: TSide;

    constructor(init: TAFieldParameters) {
        this.id = init.id ?? generateUUID();
        // Clone the nested position/scale/frameSize objects so callers
        // sharing defaults (DEFAULT_IMAGE_FIELD / DEFAULT_TEXTBOX_FIELD)
        // don't end up mutating each other through a shared reference.
        this.frameSize = init.frameSize ? { ...init.frameSize } : undefined;
        this.isLocked = init.isLocked;
        this.position = { ...init.position };
        this.rotationDeg = init.rotationDeg;
        this.scale = { ...init.scale };
        this.side = init.side;
    }

    // Whether this field carries anything to draw. Each kind answers for itself: an image needs
    // a url, a textbox needs text or a placeholder.
    //
    // Consumed by EntityController.syncFromState, which paints the repository onto a freshly
    // mounted canvas. Every reachable creation path supplies content up front — the upload
    // modal builds the image field around its url, and a new textbox always gets a placeholder
    // — so this is a guard against content that failed to arrive rather than a nominal case:
    // a saved template whose asset 404s comes back with an undefined url.
    //
    // It used to be `hasBeenPlaced`, answering "is position ≠ (0, 0)" and treating the origin
    // as a sentinel for "not centred yet". That conflated two unrelated things — whether a
    // field has content, and whether Fabric has measured and centred it — and each subclass had
    // to widen the rule with `|| this.url` / `|| this.placeholder` to undo the conflation. It
    // also lost a real field: a saved textbox with typed text, no placeholder, sitting at
    // (0, 0) read as empty and never got painted.
    abstract hasContent(): boolean;

    // A deep copy carrying a fresh id. Each kind goes through its own toJSON / fromJSON rather
    // than listing properties: the previous version copied them one by one in a composable and
    // had already drifted — ImageField.behavior was absent from the copy, which would have cost
    // a duplicated graduation its five interaction flags at once. Latent rather than lived: the
    // only control that called it was mounted by nothing. The round-trip is schema-derived, so a
    // new property cannot be forgotten here.
    abstract clone(): AField;

    // Declared here so callers serialize a field without asking what kind it is. The subclasses
    // narrow the return type, which keeps each one's schema-derived shape intact.
    abstract toJSON(): TFieldJSONData;

    moveTo(position: TPosition2D): void {
        this.position = { ...position };
    }

    setLocked(isLocked: boolean): void {
        this.isLocked = isLocked;
    }

    // Applies a full 2D view transform (position + rotation + scale)
    // from a view-side notification. TextboxField overrides to also
    // consume the optional size delta emitted when text reflows.
    applyViewTransform(transform: { position: TPosition2D; rotationDeg: number; scale: TScale2D; size?: TSize2D }): void {
        this.position = { ...transform.position };
        this.rotationDeg = transform.rotationDeg;
        this.scale = { ...transform.scale };
    }
}

export { AField };
